import { getDaysInMonth, intlFormat, setDay } from "date-fns";
import { unique } from "remeda";
import * as z from "zod/mini";
import { formatDate, getDateTimezoneOffsetInDays } from ".";
import { modulo } from "..";

export const weekdayNames = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"] as const;
export const weekdayOptions = Object.keys(weekdayNames).map(Number);
export type Weekday = (typeof weekdayNames)[number];

export const frequencyOptions = ["MONTHLY", "WEEKLY", "DAILY"] as const;
export type Frequency = (typeof frequencyOptions)[number];

export const Recurrence = z.object({
  weekdays: z.array(z.number()),
  frequency: z.enum(frequencyOptions),
  interval: z.number(),
  occurrence: z.number(),
  until: z.optional(z.date()),
});
export type Recurrence = z.infer<typeof Recurrence>;

export const repeatOptions = ["repeat", "no-repeat"] as const;
export type RepeatOption = (typeof repeatOptions)[number];

export const RecurrenceWithRepeat = z.extend(Recurrence, { repeat: z.enum(repeatOptions) });
export type RecurrenceWithRepeat = z.infer<typeof RecurrenceWithRepeat>;
/** The date's nth weekday in its month, or -1 when it's the month's last one (a 4th that's also the last is -1). */
export const getWeekdayOccurrence = (date: Date) =>
  date.getDate() + 7 > getDaysInMonth(date) ? -1 : Math.ceil(date.getDate() / 7);

export const getDefaultRecurrence = (baseDate: Date): RecurrenceWithRepeat => ({
  frequency: "WEEKLY",
  repeat: "no-repeat",
  weekdays: [baseDate.getDay()],
  interval: 1,
  occurrence: getWeekdayOccurrence(baseDate),
});

const DAY_MS = 86_400_000;
const parseVenueDate = (date: string) => Date.parse(`${date}T00:00:00Z`);
const toVenueDate = (ms: number) => new Date(ms).toISOString().slice(0, 10);

/** sidegig-api's `venue_week_start`: the picked day right after the widest gap (ties: the lowest, Monday first). */
export const getVenueWeekStart = (weekdays: Array<number>) => {
  // Monday-first order, as the server's ties break on Monday=0.
  const days = unique(weekdays).toSorted((a, b) => modulo(a - 1, 7) - modulo(b - 1, 7));
  const gap = (index: number) => modulo(days[index] - days.at(index - 1)!, 7) || 7;
  return days.reduce((best, day, index) => (gap(index) > gap(days.indexOf(best)) ? day : best), days[0]);
};

/**
 * A series' venue calendar dates ("YYYY-MM-DD") from `firstDate` through `lastDate`, both inclusive, as
 * sidegig-api's `venue_series_start_times` posts them for a series marked `weekdays_are_local`. Weekdays are Sun=0.
 */
export function getVenueSeriesDates(
  { frequency, interval, weekdays, occurrence }: Pick<Recurrence, "frequency" | "interval" | "weekdays" | "occurrence">,
  firstDate: string,
  lastDate: string,
): Array<string> {
  if (!weekdays.length) return [];
  const last = parseVenueDate(lastDate);
  let first = parseVenueDate(firstDate);
  // The first day snaps forward to the first picked weekday on or after it.
  first += Math.min(...weekdays.map((day) => modulo(day - new Date(first).getUTCDay(), 7))) * DAY_MS;
  const dates: Array<string> = [];
  if (frequency === "MONTHLY") {
    const start = new Date(first);
    for (let month = 0; ; month += interval) {
      const monthStart = Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + month, 1);
      if (monthStart > last) break;
      const daysInMonth = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth() + month + 1, 0)).getUTCDate();
      const matches = weekdays.map((day) => {
        const firstOfDay = 1 + modulo(day - new Date(monthStart).getUTCDay(), 7);
        const dayOfMonth =
          occurrence === -1
            ? firstOfDay + Math.floor((daysInMonth - firstOfDay) / 7) * 7
            : firstOfDay + (occurrence - 1) * 7;
        return dayOfMonth <= daysInMonth ? monthStart + (dayOfMonth - 1) * DAY_MS : Number.NaN;
      });
      for (const ms of matches.toSorted((a, b) => a - b)) if (ms >= first && ms <= last) dates.push(toVenueDate(ms));
    }
    return dates;
  }
  const weekStart = getVenueWeekStart(weekdays);
  const blockStart = (ms: number) => ms - modulo(new Date(ms).getUTCDay() - weekStart, 7) * DAY_MS;
  const period = (ms: number) => (frequency === "DAILY" ? ms / DAY_MS : blockStart(ms) / (7 * DAY_MS));
  for (let ms = first; ms <= last; ms += DAY_MS) {
    const periods = Math.round(period(ms) - period(first));
    if (periods % interval === 0 && weekdays.includes(new Date(ms).getUTCDay())) dates.push(toVenueDate(ms));
  }
  return dates;
}

const frequencyVerbiageLookup: Record<Frequency, string> = {
  MONTHLY: "month",
  WEEKLY: "week",
  DAILY: "day",
};
const numberVerbiageLookup = ["", "first", "second", "third", "fourth"] as const;
const getOccurrenceVerbiage = (occurrence: number) => (occurrence > 0 ? numberVerbiageLookup[occurrence] : "last");
export const getWeekdayVerbiage = (weekday: number) => intlFormat(setDay(new Date(), weekday), { weekday: "short" });

/**
 * Converts a {@linkcode timezone} weekday number to a UTC weekday number
 * @param weekday weekday number
 * @param date date in {@linkcode timezone} to derive offset from
 * @param timezone timezone to derive UTC offset from
 * @returns weekday number in UTC
 */
export const convertWeekdayToTimezone = (weekday: number, date: Date, timezone: string) =>
  modulo(weekday - getDateTimezoneOffsetInDays(date, timezone), 7);

/**
 * Converts a UTC weekday number to a {@linkcode timezone} weekday number
 * @param weekday weekday number
 * @param date date in UTC to derive offset from
 * @param timezone timezone to derive UTC offset from
 * @returns weekday number in {@linkcode timezone}
 */
export const convertWeekdayToUTC = (weekday: number, date: Date, timezone: string) =>
  modulo(weekday + getDateTimezoneOffsetInDays(date, timezone), 7);

export function getRecurrenceVerbiage(recurrence: RecurrenceWithRepeat) {
  const { occurrence, weekdays, frequency, interval, until, repeat } = recurrence;
  if (repeat === "no-repeat") return "Does not repeat";
  const frequencyVerbiage = frequencyVerbiageLookup[frequency];
  let intervalVerbiage = interval === 1 ? `each ${frequencyVerbiage}` : `every ${interval} ${frequencyVerbiage}s`;
  const allWeek = weekdays.length === 7;
  const weekdaysVerbiage = allWeek
    ? "Every day"
    : weekdays
        .toSorted((a, b) => a - b)
        .map(getWeekdayVerbiage)
        .join(", ");
  const untilString = until ? `until ${formatDate(until, "MMM d")}` : "";

  let verbiage = "";

  if (frequency === "MONTHLY") {
    if (weekdays.length === 1) {
      verbiage = `Every ${getOccurrenceVerbiage(occurrence)} ${weekdaysVerbiage} ${intervalVerbiage} ${untilString}`;
    } else {
      intervalVerbiage = interval === 1 ? "of the month" : intervalVerbiage;
      verbiage = `${weekdaysVerbiage} every ${getOccurrenceVerbiage(
        occurrence,
      )} week ${intervalVerbiage} ${untilString}`;
    }
  } else {
    verbiage = `${weekdaysVerbiage} ${allWeek ? "of the week" : ""} ${intervalVerbiage} ${untilString}`;
  }

  return verbiage.replaceAll(/\s+/g, " ").trim();
}
